import { useTracking } from '@repo/tracking/use-tracking';
import useSWRImmutable from 'swr/immutable';

import type { FeatureFlagKey, FeatureFlag } from '@repo/types/feature-flag';

/**
 * Custom hook to fetch the status of a feature flag.
 * Given a `featureKey`, it returns the corresponding `FeatureFlag` object which contains the flag's status and variation key.
 * If the flag is considered a running experiment (enabled with a variation key different from 'on'), it calls the tracking function.
 * The hook also accepts an optional `condition` parameter that can be used to control whether the experiment should be tracked or not:
 * this parameter represents an extra condition, external to the feature flag, that must be satisfied in order to consider the experiment
 * as running (hence calling the tracking function). As an example, consider an experiment that in the test variation shows certain data
 * that might be available or not: in this case, we don't want to track the experiment as running if we are not able to show the test
 * variation because the data is missing.
 */
export const useFeatureFlag = (featureKey: FeatureFlagKey, condition: boolean = true) => {
  const { trackExperiment } = useTracking();

  return useSWRImmutable(
    [`/protected/api/loyalty-management/feature/${featureKey}`, condition],
    async ([url]): Promise<FeatureFlag> => {
      const response = await fetch(url, { signal: AbortSignal.timeout(1000) });
      if (response.status >= 400) {
        throw new Error(`Error fetching feature flag: ${response.statusText}`);
      }
      return await response.json();
    },
    {
      shouldRetryOnError: false,
      onSuccess: (data) => {
        if (data.enabled && data.variationKey !== 'on' && condition) {
          trackExperiment({ featureKey, variationKey: data.variationKey });
        }
      },
    },
  );
};
